/**
 * Operations-section read contracts — RO-3-06 / #44299.
 *
 * Port of `excellent/app/operations/_dashboard.tsx` (server component). That
 * page reads money accounts, transactions, hiring jobs/applications/stages, and
 * scoped tasks **in-process** while rendering. A Vite client cannot, so the
 * vault read moves behind this Operation and the dashboard becomes a client
 * component on `useApi`.
 *
 * Live Next call sites stay on their server paths (PRD D6 — do not repoint).
 * All ops are pure GET / pure SELECT. `READ` is honest, not merely method-shaped.
 */

import { z } from "zod";
import type { Operation } from "../types";

const ledgerTxn = z.object({
  id: z.string(),
  at: z.string(),
  description: z.string(),
  amount: z.number(),
});
export type OperationsLedgerTxn = z.infer<typeof ledgerTxn>;

const ledgerRole = z.object({
  id: z.string(),
  title: z.string(),
  openedAt: z.string().nullable(),
  pipeline: z.string(),
});
export type OperationsLedgerRole = z.infer<typeof ledgerRole>;

/**
 * Enough of `ScopedTask` for `ScopedTaskLifecycleList` — id/title/status/
 * priority/dates/project+section labels. Extra columns passthrough so a future
 * list enrichment does not break the contract on the first new field.
 */
const scopedTaskDto = z
  .object({
    id: z.number(),
    uid: z.string().optional(),
    title: z.string(),
    description: z.string().nullable().optional(),
    status: z.string(),
    priority: z.string(),
    assignee: z.string().nullable().optional(),
    assignee_label: z.string().nullable().optional(),
    due_date: z.string().nullable().optional(),
    completed_at: z.string().nullable().optional(),
    updated_at: z.string(),
    created_at: z.string().optional(),
    project_slug: z.string(),
    project_name: z.string(),
    project_color: z.string().nullable().optional(),
    section_slug: z.string(),
    section_name: z.string(),
    goal_id: z.number().nullable().optional(),
    goal_slug: z.string().nullable().optional(),
    goal_title: z.string().nullable().optional(),
  })
  .passthrough();
export type OperationsScopedTaskDto = z.infer<typeof scopedTaskDto>;

const dashboardResponse = z.object({
  hasMoney: z.boolean(),
  /** Whole dollars (transaction minor units ÷ 100). */
  cash: z.number(),
  monthlyBurn: z.number(),
  runwayMonths: z.number().nullable(),
  runwayEnds: z.string().nullable(),
  recentTxns: z.array(ledgerTxn),
  spendTxns: z.array(ledgerTxn),
  roles: z.array(ledgerRole),
  hasHiring: z.boolean(),
  openJobs: z.number(),
  candidateCount: z.number(),
  offersOut: z.number(),
  thinPipeline: z.boolean(),
  tasks: z.array(scopedTaskDto),
});
export type OperationsDashboardResponse = z.infer<typeof dashboardResponse>;

const dashboardOp: Operation<void, void, void, OperationsDashboardResponse> = {
  method: "GET",
  path: () => "/api/operations/dashboard",
  response: dashboardResponse,
};

export const operations = {
  dashboard: dashboardOp,
};

export const OPERATIONS_READ_OPS = [operations.dashboard];
